import React, { useEffect, useState } from 'react';
import { View, Text, Pressable, StyleSheet, TouchableOpacity, TextInput, Dimensions, ScrollView, Icon } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { createVersion, updateVersionInfo, loadUserEmailData, loadUserEmailDataSQL, updateUserDataArray, deleteUserData, loadOverheadData, loadTeamUserDataMain, pNum, fNum, loadEquipmentData, loadUserData } from '../db/company';
import * as XLSX from 'xlsx';
import { dataSourceVersionAtom, testJobsAtom, overheadDataAtom, selectedVersionAtom, selectedJobAtom, selectedVersionLockedAtom, dataSourceJobsAtom, versionUnlocksAtom, userNameAtom, userEmailCheckAtom } from '../store/company';
import { ALL_JOBS, applyVersionUnlock, endOfToday, openUnlocksFor, saveVersionUnlock } from '../db/versionUnlock';
import moment from 'moment';
import { AI_PROMPT_STORAGE_KEY, AI_PROMPT_SECTIONS, defaultPromptFor, sectionStorageKey, userPromptStorageKey, loadServerPrompts, saveServerPrompt, clearServerPrompt } from './AISummary';
// import Budgets from './budgets';
// import TestJobs from './testJobs';

const Admin = () => {
    const [versions, setVersions] = useAtom(dataSourceVersionAtom)
    const [versionCleaned, setVersionCleaned] = useState([])
    const [versionDropdown, setVersionDropdown] = useState([])
    const [waiting, setWaiting] = useState(false)
    const [activeTab, setActiveTab] = useState('Versions');
    const tabs = ['Versions', 'Users', 'OH Values', 'Equipment Rates', 'Resource Code', /* 'Budgets', 'Test Jobs', */ 'Overview Assignments', 'AI'];
    const [pageWidth, setPageWidth] = useState(Dimensions.get('window').width);
    const [userEmails, setUserEmails] = useState([]);
    const [newEmail, setNewEmail] = useState('');
    const [overheadData] = useAtom(overheadDataAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [selectedJob] = useAtom(selectedJobAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    // Admin version unlocks (db/versionUnlock.js); main.js keeps the list fresh.
    const [versionUnlocks, setVersionUnlocks] = useAtom(versionUnlocksAtom);
    const [userName] = useAtom(userNameAtom);
    const [userEmailCheck] = useAtom(userEmailCheckAtom);
    const changeVersionUnlock = (change, question) => {
        if (!window.confirm(question)) return;
        const by = userName || userEmailCheck || '';
        saveVersionUnlock({ ...change, by });
        setVersionUnlocks((prev) => applyVersionUnlock(prev, { ...change, by }));
    };
    const [ohValues, setOhValues] = useState([]);
    const [editingOhIndex, setEditingOhIndex] = useState(null);
    const [editedPercent, setEditedPercent] = useState('');
    const [newTeamName, setNewTeamName] = useState('');
    const [newTeamPercent, setNewTeamPercent] = useState('');
    const [allJobs] = useAtom(dataSourceJobsAtom);
    const [overviewJobs, setOverviewJobs] = useState([]);
    const [overviewLoading, setOverviewLoading] = useState(false);
    const [equipmentRatesData, setEquipmentRatesData] = useState([]);
    const [equipmentLoading, setEquipmentLoading] = useState(false);
    const [equipmentUploadStatus, setEquipmentUploadStatus] = useState(null);
    const [resourceCodeData, setResourceCodeData] = useState([]);
    const [resourceCodeLoading, setResourceCodeLoading] = useState(false);
    const [resourceCodeUploadStatus, setResourceCodeUploadStatus] = useState(null);
    const [resourceMapData, setResourceMapData] = useState([]);
    const [resourceMapLoading, setResourceMapLoading] = useState(false);
    const [resourceMapUploadStatus, setResourceMapUploadStatus] = useState(null);
    const [aiSection, setAiSection] = useState(AI_PROMPT_SECTIONS[0].id);
    const [aiPromptDrafts, setAiPromptDrafts] = useState(() => {
        const drafts = {};
        AI_PROMPT_SECTIONS.forEach(({ id, legacy }) => {
            let saved = null;
            try {
                if (typeof localStorage !== 'undefined') {
                    // Only the original two sections inherit the old global
                    // key — see getSystemPrompt in AISummary.
                    saved = localStorage.getItem(sectionStorageKey(id))
                        || (legacy ? localStorage.getItem(AI_PROMPT_STORAGE_KEY) : null);
                }
            } catch { /* ignore */ }
            drafts[id] = saved || defaultPromptFor(id);
        });
        return drafts;
    });
    const [aiPromptSaveStatus, setAiPromptSaveStatus] = useState('');
    const [aiPromptSaving, setAiPromptSaving] = useState(false);
    const [aiPromptLoading, setAiPromptLoading] = useState(false);
    const [aiLastUserPrompt, setAiLastUserPrompt] = useState('');

    useEffect(() => {
        if (activeTab !== 'AI') return;
        try {
            if (typeof localStorage !== 'undefined') {
                setAiLastUserPrompt(localStorage.getItem(userPromptStorageKey(aiSection)) || '');
            }
        } catch { /* ignore */ }
    }, [activeTab, aiSection]);

    // Load the shared per-section prompts from SQL whenever the AI tab opens so
    // the editor shows what's live for everyone (not just this browser's cache).
    useEffect(() => {
        if (activeTab !== 'AI') return;
        let cancelled = false;
        setAiPromptLoading(true);
        loadServerPrompts()
            .then(server => {
                if (cancelled || !server) return;
                setAiPromptDrafts(prev => {
                    const next = { ...prev };
                    AI_PROMPT_SECTIONS.forEach(({ id }) => {
                        if (server[id] && server[id].trim()) next[id] = server[id];
                    });
                    return next;
                });
            })
            .finally(() => { if (!cancelled) setAiPromptLoading(false); });
        return () => { cancelled = true; };
    }, [activeTab]);

    useEffect(() => {
        const handleResize = () => {
            setPageWidth(Dimensions.get('window').width);
        };

        const subscription = Dimensions.addEventListener('change', handleResize);

        return () => {
            subscription?.remove();
        };
    }, []);

    useEffect(() => {
        const loadUsers = async () => {
            const result = await loadUserEmailDataSQL({});
            if (result && result.emailData && result.emailData.length > 0) {
                // Use emailData which includes GUID
                setUserEmails(result.emailData);
            } else if (result && result.emailConfig) {
                // Fallback to emailConfig (hardcoded list)
                const emailArray = Object.keys(result.emailConfig).map(email => ({
                    email,
                    active: result.emailConfig[email],
                    guid: null,
                    id: '',
                    version: ''
                }));
                setUserEmails(emailArray);
            }
        };
        loadUsers();
    }, []);

    // Load Equipment Rates
    const loadEquipmentRates = async () => {
        if (!selectedVersion) return;
        setEquipmentLoading(true);
        try {
            const data = await loadEquipmentData({ forecastGuid: selectedVersion });
            const rData = {};
            data?.data?.forEach((d) => {
                if (d?.Forecast_Tab === 'Equipment') {
                    if (rData[d?.Forecast_Row_ID]) {
                        rData[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value;
                    } else {
                        rData[d?.Forecast_Row_ID] = { id: d?.Forecast_Row_ID, [d.Forecast_Field_Name]: d.Forecast_Value };
                    }
                }
            });
            const rows = Object.values(rData).sort((a, b) => (a['Equipment Tier'] || '').localeCompare(b['Equipment Tier'] || ''));
            console.log('equipmentRatesData raw', data?.data);
            console.log('equipmentRatesData mapped', rows);
            setEquipmentRatesData(rows);
        } catch (e) {
            console.error('loadEquipmentRates error', e);
        }
        setEquipmentLoading(false);
    };

    useEffect(() => {
        if (activeTab === 'Equipment Rates' && selectedVersion) loadEquipmentRates();
    }, [activeTab, selectedVersion]);

    // Load Resource Codes — version-scoped, BU_ID='all'
    const loadResourceCodes = async () => {
        if (!selectedVersion) return;
        setResourceCodeLoading(true);
        try {
            const result = await loadUserData({ forecastGuid: selectedVersion, id: 'all' });
            const rows = (result?.data || [])
                .filter(d => d?.Forecast_Tab === 'Resource Code' && d?.BU_ID === 'all')
                .map(d => ({
                    id: d.Forecast_Row_ID,
                    'Cost Type': d.Cost_Type || '',
                    'Code': d.Forecast_Value || '',
                    'Description': d.Description || '',
                }))
                .sort((a, b) =>
                    (a['Cost Type'] || '').localeCompare(b['Cost Type'] || '') ||
                    (a['Code'] || '').localeCompare(b['Code'] || '')
                );
            setResourceCodeData(rows);
        } catch (e) {
            console.error('loadResourceCodes error', e);
        }
        setResourceCodeLoading(false);
    };

    useEffect(() => {
        if (activeTab === 'Resource Code' && selectedVersion) loadResourceCodes();
    }, [activeTab, selectedVersion]);

    // Load Resource Code Mapping — version-scoped, BU_ID='all'
    const loadResourceCodeMapping = async () => {
        if (!selectedVersion) return;
        setResourceMapLoading(true);
        try {
            const result = await loadUserData({ forecastGuid: selectedVersion, id: 'all' });
            const rows = (result?.data || [])
                .filter(d => d?.Forecast_Tab === 'Resource Code Mapping' && d?.BU_ID === 'all')
                .map(d => ({
                    id: d.Forecast_Row_ID,
                    'Cost Type': d.Cost_Type || '',
                    'Code': d.Forecast_Value || '',
                    'Description': d.Description || '',
                    'Code 2': d.Resource_Code || '',
                    'Description 2': d.Supplier_Name || '',
                }))
                .sort((a, b) =>
                    (a['Cost Type'] || '').localeCompare(b['Cost Type'] || '') ||
                    (a['Code'] || '').localeCompare(b['Code'] || '')
                );
            setResourceMapData(rows);
        } catch (e) {
            console.error('loadResourceCodeMapping error', e);
        }
        setResourceMapLoading(false);
    };

    useEffect(() => {
        if (activeTab === 'Resource Code' && selectedVersion) loadResourceCodeMapping();
    }, [activeTab, selectedVersion]);

    const uploadResourceCodeMapping = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const parsed = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            const desiredColumns = ['Cost Type', 'Code', 'Description', 'Code 2', 'Description 2'];
            const headers = parsed[0] || [];
            const colMap = {};
            desiredColumns.forEach((col) => {
                const idx = headers.indexOf(col);
                if (idx !== -1) colMap[col] = idx;
            });
            const missing = desiredColumns.filter(c => colMap[c] === undefined);
            if (missing.length) {
                setResourceMapUploadStatus('Missing columns: ' + missing.join(', '));
                return;
            }
            const rows = parsed.slice(1).filter(r => r[colMap['Code']]);
            const newGuid = () => (crypto?.randomUUID?.() || (Date.now() + '-' + Math.random().toString(36).slice(2)));
            const rowsToSave = rows.map((row, index) => ({
                userId: '154045',
                userName: 'Seth Bailey',
                fieldName: 'resourceCodeMapping',
                tab: 'Resource Code Mapping',
                rowId: (index + '').replace(/[^a-zA-Z0-9./\s\-^$()*=]/g, ''),
                version: selectedVersion + '',
                value: (row[colMap['Code']] + '').replace(/[^a-zA-Z0-9./\s\-^()*=&]/g, ''),
                costType: (row[colMap['Cost Type']] + '').replace(/[^a-zA-Z0-9./\s\-^()*=&]/g, ''),
                description: (row[colMap['Description']] + '').replace(/[^a-zA-Z0-9./\s\-^()*=&]/g, ''),
                resourceCode: (row[colMap['Code 2']] + '').replace(/[^a-zA-Z0-9./\s\-^()*=&]/g, ''),
                vendor: (row[colMap['Description 2']] + '').replace(/[^a-zA-Z0-9./\s\-^()*=&]/g, ''),
                id: 'all',
                lockedVersion: lockedVersion,
                insert: true,
                guid: newGuid(),
            }));
            await updateUserDataArray(rowsToSave);
            setResourceMapUploadStatus(rows.length + ' mapping rows uploaded successfully');
            setTimeout(() => loadResourceCodeMapping(), 1500);
        };
        reader.readAsArrayBuffer(file);
    };

    const uploadResourceCodes = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const parsed = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            const desiredColumns = ['Cost Type', 'Code', 'Description'];
            const headers = parsed[0] || [];
            const colMap = {};
            desiredColumns.forEach((col) => {
                const idx = headers.indexOf(col);
                if (idx !== -1) colMap[col] = idx;
            });
            const missing = desiredColumns.filter(c => colMap[c] === undefined);
            if (missing.length) {
                setResourceCodeUploadStatus('Missing columns: ' + missing.join(', '));
                return;
            }
            const rows = parsed.slice(1).filter(r => r[colMap['Code']]);
            const newGuid = () => (crypto?.randomUUID?.() || (Date.now() + '-' + Math.random().toString(36).slice(2)));
            const rowsToSave = rows.map((row, index) => ({
                userId: '154045',
                userName: 'Seth Bailey',
                fieldName: 'resourceCode',
                tab: 'Resource Code',
                rowId: (index + '').replace(/[^a-zA-Z0-9./\s\-^$()*=]/g, ''),
                version: selectedVersion + '',
                value: (row[colMap['Code']] + '').replace(/[^a-zA-Z0-9./\s\-^()*=&]/g, ''),
                costType: (row[colMap['Cost Type']] + '').replace(/[^a-zA-Z0-9./\s\-^()*=&]/g, ''),
                description: (row[colMap['Description']] + '').replace(/[^a-zA-Z0-9./\s\-^()*=&]/g, ''),
                id: 'all',
                lockedVersion: lockedVersion,
                insert: true,
                guid: newGuid(),
            }));
            await updateUserDataArray(rowsToSave);
            setResourceCodeUploadStatus(rows.length + ' resource codes uploaded successfully');
            setTimeout(() => loadResourceCodes(), 1500);
        };
        reader.readAsArrayBuffer(file);
    };

    const uploadEquipmentRates = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const parsed = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            const desiredColumns = ['Equipment Tier', 'Asset Description', 'Current Hourly Billing Rate', 'Current Daily Billing Rate', 'Current Weekly Billing Rate'];
            const colMap = {};
            const headers = parsed[0] || [];
            desiredColumns.forEach((col) => {
                const idx = headers.indexOf(col);
                if (idx !== -1) colMap[col] = idx;
            });
            const missing = desiredColumns.filter(c => colMap[c] === undefined);
            if (missing.length) {
                setEquipmentUploadStatus('Missing columns: ' + missing.join(', '));
                return;
            }
            const rows = parsed.slice(1).filter(r => r[colMap['Equipment Tier']]);
            const rowsToSave = [];
            rows.forEach((row, index) => {
                desiredColumns.forEach((col) => {
                    if (row[colMap[col]] != null && row[colMap[col]] !== '') {
                        rowsToSave.push({
                            userId: '154045',
                            userName: 'Seth Bailey',
                            fieldName: (col + '').replace(/[^a-zA-Z0-9./\s\-^()*=]/g, ''),
                            tab: 'Equipment',
                            rowId: (index + '').replace(/[^a-zA-Z0-9./\s\-^$()*=]/g, ''),
                            version: selectedVersion + '',
                            value: (row[colMap[col]] + '').replace(/[^a-zA-Z0-9./\s\-^()*=]/g, ''),
                            id: selectedJob,
                            lockedVersion: lockedVersion
                        });
                    }
                });
            });
            console.log('rowsToSave', rowsToSave.slice(0, 5));
            await updateUserDataArray(rowsToSave);
            setEquipmentUploadStatus(rows.length + ' equipment tiers uploaded successfully');
            // Clear localStorage cache so reload fetches fresh data
            localStorage.removeItem(`equipmentRates_${selectedVersion}`);
            setTimeout(() => loadEquipmentRates(), 2000);
        };
        reader.readAsArrayBuffer(file);
    };

    // Load OH Values
    const loadOHValues = async () => {
        if (selectedVersion) {
            const data = await loadOverheadData({ forecastGuid: selectedVersion });
            const ohValuesMap = {}
            data?.data?.forEach((d) => {
                if (ohValuesMap[d?.Forecast_Row_ID]) {
                    ohValuesMap[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value;
                    // Store GUID for each field
                    if (d.Forecast_Field_Name === 'team') {
                        ohValuesMap[d?.Forecast_Row_ID].teamGuid = d?.Forecast_User_Data_GUID;
                    } else if (d.Forecast_Field_Name === 'percent') {
                        ohValuesMap[d?.Forecast_Row_ID].percentGuid = d?.Forecast_User_Data_GUID;
                    }
                } else {
                    ohValuesMap[d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        [d.Forecast_Field_Name]: d.Forecast_Value,
                        teamGuid: d.Forecast_Field_Name === 'team' ? d?.Forecast_User_Data_GUID : '',
                        percentGuid: d.Forecast_Field_Name === 'percent' ? d?.Forecast_User_Data_GUID : ''
                    }
                }
            })
            
            // Convert to array for table display, including rowId and both GUIDs
            const ohArray = Object.values(ohValuesMap).map(value => ({
                rowId: value.id || '',
                teamGuid: value.teamGuid || '',
                percentGuid: value.percentGuid || '',
                team: value.team || '',
                percent: value.percent || ''
            }));
            setOhValues(ohArray);
        }
    };

    useEffect(() => {
        loadOHValues();
    }, [selectedVersion]);

    // Overview Assignments loading
    const loadOverviewAssignments = async () => {
        if (!allJobs || !selectedVersion) return;
        setOverviewLoading(true);
        try {
            const activeJobs = Array.isArray(allJobs) ? allJobs.filter(j => !['JW', 'JP', 'JX'].includes(j.Cat_Code_BU_08)) : [];
            const jobIds = activeJobs.map(j => j.BU_ID);
            if (jobIds.length === 0) { setOverviewLoading(false); return; }
            const teamData = await loadTeamUserDataMain({ ids: jobIds, forecastGuid: selectedVersion });
            const userData = teamData?.data || [];

            const contractValues = {};
            const indicators = {};

            userData.forEach(d => {
                const jobId = d?.BU_ID;
                if (!jobId) return;
                if (!contractValues[jobId]) contractValues[jobId] = 0;
                if (!indicators[jobId]) indicators[jobId] = { laborWeeks: false, milestones: false, safety: false, notes: false, reviewed: false, pctComplete: '' };

                // Day Zero Budget revenue (Cost_Type starting with '4')
                if (d.Forecast_Tab === 'Day Zero Budget' && d.Cost_Type?.toString().startsWith('4')) {
                    contractValues[jobId] += pNum(d.Value_Amount || 0);
                }
                // Change Order approved revenue
                if (d.Forecast_Tab === 'Change Order' && d.Status === 'Approved' && d.Cost_Type === '41000') {
                    contractValues[jobId] += pNum(d.Value_Amount || 0);
                }
                // Indicator: Labor Weeks (Phase Hours tab)
                if (d.Forecast_Tab === 'Phase Hours' && d.Forecast_Field_Name?.startsWith('week_') && pNum(d.Forecast_Value) > 0) {
                    indicators[jobId].laborWeeks = true;
                }
                // Indicator: Milestones
                if (d.Forecast_Tab === 'Overview' && d.Forecast_Field_Name === 'Milestone') {
                    indicators[jobId].milestones = true;
                }
                // Indicator: Safety Metrics
                if (d.Forecast_Tab === 'Overview' && ['SafetyFirstAid', 'SafetyIncidents', 'SafetyNearMiss', 'SafetyStopWork', 'SafetyQAQC'].includes(d.Forecast_Field_Name) && d.Forecast_Value && d.Forecast_Value.toString().trim()) {
                    indicators[jobId].safety = true;
                }
                // Indicator: Notes
                if (d.Forecast_Tab === 'Overview' && ['ChangeOrderNotes', 'OverviewNotes', 'BillingNotes'].includes(d.Forecast_Field_Name) && d.Forecast_Value && d.Forecast_Value.toString().trim()) {
                    indicators[jobId].notes = true;
                }
                // Reviewed status
                if (d.Forecast_Tab === 'Overview Assignments' && d.Forecast_Field_Name === 'reviewed') {
                    indicators[jobId].reviewed = d.Forecast_Value === 'true';
                }
                // Percent Complete
                if ((d.Forecast_Tab === 'Forecast' || d.Forecast_Tab === 'All') && d.Forecast_Field_Name === 'physicalComplete' && d.Forecast_Value) {
                    indicators[jobId].pctComplete = d.Forecast_Value;
                }
            });

            const jobList = activeJobs
                .filter(j => (contractValues[j.BU_ID] || 0) >= 3000000)
                .map(j => ({
                    buId: j.BU_ID,
                    name: j.BU_Description || j.Job_Description || '',
                    pm: j.Project_Manager_Name || '',
                    contractValue: contractValues[j.BU_ID] || 0,
                    laborWeeks: indicators[j.BU_ID]?.laborWeeks || false,
                    milestones: indicators[j.BU_ID]?.milestones || false,
                    safety: indicators[j.BU_ID]?.safety || false,
                    notes: indicators[j.BU_ID]?.notes || false,
                    reviewed: indicators[j.BU_ID]?.reviewed || false,
                    pctComplete: indicators[j.BU_ID]?.pctComplete || '',
                }))
                .sort((a, b) => {
                    // Included (reviewed) first
                    if (a.reviewed !== b.reviewed) return a.reviewed ? -1 : 1;
                    // Then lowest percent complete to highest
                    const aPct = parseFloat(a.pctComplete) || 0;
                    const bPct = parseFloat(b.pctComplete) || 0;
                    return aPct - bPct;
                });

            setOverviewJobs(jobList);
        } catch (e) {
            console.error('Error loading overview assignments:', e);
        }
        setOverviewLoading(false);
    };

    useEffect(() => {
        if (activeTab === 'Overview Assignments') {
            loadOverviewAssignments();
        }
    }, [activeTab, selectedVersion]);

    const saveReviewedStatus = async (buId, reviewed) => {
        setOverviewJobs(prev => prev.map(j => j.buId === buId ? { ...j, reviewed } : j));
        await updateUserDataArray([{
            userId: '154045',
            userName: 'Admin',
            tab: 'Overview Assignments',
            rowId: buId,
            version: selectedVersion || '',
            id: buId,
            lockedVersion: false,
            fieldName: 'reviewed',
            value: reviewed ? 'true' : 'false',
            rowLabel: buId
        }]);
    };

    useEffect(() => {
        console.log('versions', versions)
        const cleaned = []
        const dropdown = []
        versions && versions.forEach(element => {
            cleaned.push({
                id: element.Version_GUID,
                start: element.Version_Start_DateTime ? moment(element.Version_Start_DateTime).format('YYYY-MM-DD HH:MM') : '',
                finish: element.Version_Finish_DateTime ? moment(element.Version_Finish_DateTime).format('YYYY-MM-DD HH:MM') : '',
                description: element.Version_Status_Description,
                type: element.Version_Type,
                notes: element.Version_Notes,
                archived: element.Version_Archived || false
            })

            dropdown.push({
                label: '(' + element.Version_GUID + ') ' + moment(element.Version_Start_DateTime).format('M/D/YY H:MM'),
                value: element.Version_GUID
            })
        });
        dropdown.sort((a, b) => {
            if (a.value > b.value) {
                return -1
            }
            if (a.value < b.value) {
                return 1
            }
            return 0
        })
        cleaned.sort((a, b) => {
            if (a.id > b.id) {
                return -1
            }
            if (a.id < b.id) {
                return 1
            }
            return 0
        })
        setVersionDropdown(dropdown)
        setVersionCleaned(cleaned)
    }, [versions])

    const versionOptions = [
        { label: 'WIP', value: 'WIP' },
        { label: 'Regular', value: 'Regular' },
        { label: 'Reg-locked', value: 'Reg-locked' },
        { label: 'Hidden', value: 'Hidden' },
        { label: 'Unassigned', value: 'Unassigned' },
        { label: 'WIP-hidden', value: 'WIP-hidden' },
        { label: 'WIP-locked', value: 'WIP-locked' },
    ]


    const s = StyleSheet.create({
        cell: {
            height: 32,
            borderWidth: 1,
            borderColor: '#aeadaf',
            padding: 4,
            textAlign: 'center',
        },
        dropdown: {
            height: 32,
            width: 200,
            borderColor: 'white',
            paddingHorizontal: 8,
            borderColor: '#aeadaf',
            borderWidth: 1,
        },
        placeholderStyle: {
            fontSize: 16,
            fontWeight: '300',
        },
        selectedTextStyle: {
            fontSize: 16,
            color: 'black'
        },
        container: {
            flex: 1,
            backgroundColor: '#F5F7FA',
            paddingLeft: 20
        },
        tabItem: {
            paddingVertical: 12,
            paddingHorizontal: 16,
            borderBottomWidth: 2,
            borderBottomColor: 'transparent',
        },
        activeTab: {
            borderBottomColor: '#192440',
        },
        tabText: {
            fontSize: 16,
            color: '#888',
        },
        activeText: {
            color: '#000',
            fontWeight: '600',
        },
        content: {
            padding: 20,
        },
        contentText: {
            fontSize: 16,
        },
    });

    const Row = ({ v }) => {
        const [status, setStatus] = useState(v.type)
        const [userData, setUserData] = useState(v.type)
        const [userNotes, setUserNotes] = useState(v.notes)
        const [archived, setArchived] = useState(v.archived)
        const [isFocusTwo, setIsFocusTwo] = useState(false)
        const [isFocus, setIsFocus] = useState(false)
        const saveType = (t = null, arch = null) => {
            console.log('saveType', v.id, status, userNotes, archived)
            updateVersionInfo({ 
                id: v.id + '', 
                type: t ? t : status + '', 
                notes: (userNotes ? userNotes : '') + '',
                archived: arch !== null ? (arch ? 1 : 0) : (archived ? 1 : 0)
            })
        }
        return (
            <View style={{ 
                flexDirection: 'row', 
                backgroundColor: v.index % 2 === 0 ? '#ffffff' : '#f9fafb',
                paddingVertical: 8,
                paddingHorizontal: 10,
                borderBottom: '1px solid #e2e8f0'
            }}>
                <Text style={[s.cell, { width: 80, color: '#334155' }]}>{v.id}</Text>
                <Text style={[s.cell, { width: 200, color: '#334155' }]}>{v.start}</Text>
                <Text style={[s.cell, { width: 200, color: '#334155' }]}>{v.finish}</Text>
                <Text style={[s.cell, { width: 300, color: '#334155' }]}>{v.description}</Text>
                <select
                    style={{
                        height: 32,
                        width: 200,
                        borderColor: '#aeadaf',
                        paddingHorizontal: 8,
                        borderWidth: 1,
                        fontSize: 16,
                        color: 'black'
                    }}
                    value={status}
                    onChange={(e) => {
                        console.log('status changed', e.target.value);
                        setStatus(e.target.value);
                        saveType(e.target.value);
                    }}
                >
                    {versionOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                {/* <Dropdown
                style={[s.dropdown, isFocusTwo && { borderColor: 'blue' }]}
                placeholderStyle={s.placeholderStyle}
                selectedTextStyle={s.selectedTextStyle}
                data={versionDropdown}
                maxHeight={300}
                labelField="label"
                valueField="value"
                placeholder={!isFocus ? 'Select Version' : '...'}
                value={status}
                onFocus={() => setIsFocusTwo(true)}
                onBlur={() => setIsFocusTwo(false)}
                onChange={item => {
                    console.log('userdata version changed', item.value)
                    setUserData(item.value);
                    saveVersionUserData(item.value)
                    setIsFocusTwo(false);
                }}
            /> */}
                <TextInput
                    style={[s.cell, { width: 300, textAlign: 'left' }]}
                    value={userNotes}
                    onChangeText={(text) => setUserNotes(text)}
                    onBlur={() => saveType()}>
                </TextInput>
                <View style={[s.cell, { width: 100, alignItems: 'center', justifyContent: 'center' }]}>
                    <input
                        type="checkbox"
                        checked={archived}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            setArchived(newValue);
                            saveType(null, newValue);
                        }}
                        style={{ width: 20, height: 20, cursor: 'pointer' }}
                    />
                </View>
                <View style={[s.cell, { width: 300, height: 'auto', minHeight: 32, alignItems: 'flex-start', justifyContent: 'center', gap: 2 }]}>
                    {openUnlocksFor(versionUnlocks, v.id).map((u) => (
                        <Text key={u.job} style={{ fontSize: 12, color: '#B45309' }} title={u.by ? `Unlocked by ${u.by}` : undefined}>
                            {`${u.job === ALL_JOBS ? 'All jobs' : u.job} until ${moment(u.until).format('h:mm A')} · `}
                            <Text
                                style={{ color: '#0b142e', textDecoration: 'underline', cursor: 'pointer' }}
                                onClick={() => changeVersionUnlock(
                                    { version: v.id, job: u.job, until: null },
                                    `Lock version ${v.id} again for ${u.job === ALL_JOBS ? 'all jobs' : `job ${u.job}`}?`
                                )}
                            >Lock</Text>
                        </Text>
                    ))}
                    {!openUnlocksFor(versionUnlocks, v.id).some((u) => u.job === ALL_JOBS) ? (
                        <Text
                            style={{ fontSize: 12, color: '#475569', textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={() => changeVersionUnlock(
                                { version: v.id, job: ALL_JOBS, until: endOfToday() },
                                `Unlock version ${v.id} for all jobs until 11:59 PM tonight?\n\nEdits made on it stay on that version. They don't carry into the live version.`
                            )}
                        >Unlock all jobs</Text>
                    ) : null}
                </View>
            </View>
        )
    }

    const createVersionAction = async () => {
        setWaiting(true)
        await createVersion({})
        setWaiting(false)
    }

    // Helper function to generate random 8-digit number
    const generateRandomId = () => {
        return Math.floor(10000000 + Math.random() * 90000000).toString();
    };

    const saveUserEmail = async (email, active) => {
        const rowId = email + '_' + generateRandomId();
        const userData = {
            userId: '154045',
            userName: 'Admin',
            tab: "User Email Configuration",
            rowId: rowId,
            version: '',
            id: '',
            lockedVersion: false,
            fieldName: 'Row_Label',
            value: email,
            valueAmount: active ? 1 : 0,
            rowLabel: email
        };
        await updateUserDataArray([userData]);
    };

    const deleteUserEmail = async (user) => {
        console.log('user', user)
        // await deleteUserData({
        //     rowId: user.guid,
        //     id: user.id || '',
        //     forecastGuid: user.version || '',
        //     tab: 'User Email Configuration'
        // });
    };

    // OH Values handlers
    const saveOhPercent = async (rowId, teamName, percent) => {
        const userData = [
            {
                userId: '154045',
                userName: 'Admin',
                tab: 'Overhead',
                rowId: rowId,
                version: selectedVersion || '',
                id: '',
                lockedVersion: false,
                fieldName: 'team',
                value: teamName,
                rowLabel: teamName
            },
            {
                userId: '154045',
                userName: 'Admin',
                tab: 'Overhead',
                rowId: rowId,
                version: selectedVersion || '',
                id: '',
                lockedVersion: false,
                fieldName: 'percent',
                value: percent,
                rowLabel: teamName
            }
        ];
        console.log('saveOhPercent', userData)
        await updateUserDataArray(userData);
        setEditingOhIndex(null);
    };

    const addTeam = async () => {
        if (newTeamName.trim() && newTeamPercent.trim()) {
            // Generate next highest rowId
            const existingIds = ohValues.map(oh => parseInt(oh.rowId) || 0).filter(id => !isNaN(id));
            const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
            
            // Save to database
            await saveOhPercent(nextId.toString(), newTeamName, newTeamPercent);
            
            // Reload OH values to get the new data with GUIDs
            await loadOHValues();
            
            setNewTeamName('');
            setNewTeamPercent('');
        }
    };

    const deleteTeam = async (index) => {
        if (window.confirm(`Delete team "${ohValues[index].team}"?`)) {
            const ohValue = ohValues[index];
            // Delete both 'team' and 'percent' records using their GUIDs
            if (ohValue.teamGuid) {
                await deleteUserData({
                    id: selectedJob,
                    forecastGuid: selectedVersion,
                    lockedVersion: lockedVersion,
                    rowId: ohValue.teamGuid + '',
                    tab: 'Overhead'
                });
            }
            if (ohValue.percentGuid) {
                await deleteUserData({
                    id: selectedJob,
                    forecastGuid: selectedVersion,
                    lockedVersion: lockedVersion,
                    rowId: ohValue.percentGuid + '',
                    tab: 'Overhead'
                });
            }
            const newOhValues = ohValues.filter((_, i) => i !== index);
            setOhValues(newOhValues);
        }
    };

    return (
        <View>
            <View style={[s.container, { minWidth: pageWidth - 300, paddingTop: 8 }]}>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    {tabs.map((tab) => (
                        <Pressable
                            key={tab}
                            onPress={() => setActiveTab(tab)}
                            style={{
                                paddingVertical: 16,
                                paddingHorizontal: 24,
                                borderBottom: activeTab === tab ? '1px solid #2563eb' : '4px solid transparent',
                                backgroundColor: activeTab === tab ? '#eff6ff' : 'transparent',
                                borderTopLeftRadius: 6,
                                borderTopRightRadius: 6,
                            }}
                        >
                            <Text style={{
                                padding: 10,
                                fontSize: 16,
                                color: activeTab === tab ? '#1e40af' : '#64748b',
                                fontWeight: '300',
                                letterSpacing: 0.3,
                            }}>
                                {tab}
                            </Text>
                        </Pressable>
                    ))}
                </ScrollView>
            </View>
            {activeTab === 'Overview Assignments' ? (
                <View style={{ margin: 20 }}>
                    <View style={{
                        backgroundColor: 'white',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                        padding: 20
                    }}>
                        <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 6 }}>Overview Assignments</Text>
                        <Text style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
                            Jobs with contract value over $3M. Track overview review status and completion indicators.
                        </Text>
                        {overviewLoading ? (
                            <Text style={{ fontSize: 14, color: '#64748b', padding: 20 }}>Loading...</Text>
                        ) : overviewJobs.length === 0 ? (
                            <Text style={{ fontSize: 14, color: '#64748b', padding: 20 }}>No jobs found with contract value over $3M.</Text>
                        ) : (
                            <>
                                <View style={{
                                    flexDirection: 'row',
                                    padding: 12,
                                    backgroundColor: '#f8fafc',
                                    borderRadius: 6,
                                    borderBottom: '2px solid #e2e8f0',
                                    marginBottom: 4
                                }}>
                                    <Text style={[s.cell, { width: 100, fontWeight: '600', color: '#0f172a', textAlign: 'left' }]}>Job #</Text>
                                    <Text style={[s.cell, { width: 260, fontWeight: '600', color: '#0f172a', textAlign: 'left' }]}>Job Name</Text>
                                    <Text style={[s.cell, { width: 160, fontWeight: '600', color: '#0f172a', textAlign: 'left' }]}>PM</Text>
                                    <Text style={[s.cell, { width: 140, fontWeight: '600', color: '#0f172a', textAlign: 'right' }]}>Contract Value</Text>
                                    <Text style={[s.cell, { width: 80, fontWeight: '600', color: '#0f172a', textAlign: 'center' }]}>% Comp</Text>
                                    <Text style={[s.cell, { width: 80, fontWeight: '600', color: '#0f172a', textAlign: 'center' }]}>Included</Text>
                                    <Text style={[s.cell, { width: 90, fontWeight: '600', color: '#0f172a', textAlign: 'center' }]}>Labor{`\n`}Weeks</Text>
                                    <Text style={[s.cell, { width: 90, fontWeight: '600', color: '#0f172a', textAlign: 'center' }]}>Milestones</Text>
                                    <Text style={[s.cell, { width: 90, fontWeight: '600', color: '#0f172a', textAlign: 'center' }]}>Safety</Text>
                                    <Text style={[s.cell, { width: 90, fontWeight: '600', color: '#0f172a', textAlign: 'center' }]}>Notes</Text>
                                </View>
                                <ScrollView style={{ maxHeight: 700 }}>
                                    {overviewJobs.map((job, index) => (
                                        <View key={job.buId} style={{
                                            flexDirection: 'row',
                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                            paddingVertical: 8,
                                            paddingHorizontal: 10,
                                            borderBottom: '1px solid #e2e8f0',
                                            alignItems: 'center'
                                        }}>
                                            <Text style={[s.cell, { width: 100, color: '#334155', textAlign: 'left', fontWeight: '500' }]}>{job.buId}</Text>
                                            <Text style={[s.cell, { width: 260, color: '#334155', textAlign: 'left' }]} numberOfLines={1}>{job.name}</Text>
                                            <Text style={[s.cell, { width: 160, color: '#334155', textAlign: 'left' }]} numberOfLines={1}>{job.pm}</Text>
                                            <Text style={[s.cell, { width: 140, color: '#334155', textAlign: 'right', fontWeight: '600' }]}>{fNum(job.contractValue, 1)}</Text>
                                            <Text style={[s.cell, { width: 80, color: '#334155', textAlign: 'center' }]}>{job.pctComplete ? parseFloat(job.pctComplete).toFixed(0) + '%' : '-'}</Text>
                                            <View style={[s.cell, { width: 80, alignItems: 'center', justifyContent: 'center' }]}>
                                                <input
                                                    type="checkbox"
                                                    checked={job.reviewed}
                                                    onChange={(e) => saveReviewedStatus(job.buId, e.target.checked)}
                                                    style={{ width: 20, height: 20, cursor: 'pointer' }}
                                                />
                                            </View>
                                            <View style={[s.cell, { width: 90, alignItems: 'center', justifyContent: 'center' }]}>
                                                <Text style={{ fontSize: 16, color: job.laborWeeks ? '#16a34a' : '#d1d5db', fontWeight: '700' }}>{job.laborWeeks ? '\u2713' : '\u2717'}</Text>
                                            </View>
                                            <View style={[s.cell, { width: 90, alignItems: 'center', justifyContent: 'center' }]}>
                                                <Text style={{ fontSize: 16, color: job.milestones ? '#16a34a' : '#d1d5db', fontWeight: '700' }}>{job.milestones ? '\u2713' : '\u2717'}</Text>
                                            </View>
                                            <View style={[s.cell, { width: 90, alignItems: 'center', justifyContent: 'center' }]}>
                                                <Text style={{ fontSize: 16, color: job.safety ? '#16a34a' : '#d1d5db', fontWeight: '700' }}>{job.safety ? '\u2713' : '\u2717'}</Text>
                                            </View>
                                            <View style={[s.cell, { width: 90, alignItems: 'center', justifyContent: 'center' }]}>
                                                <Text style={{ fontSize: 16, color: job.notes ? '#16a34a' : '#d1d5db', fontWeight: '700' }}>{job.notes ? '\u2713' : '\u2717'}</Text>
                                            </View>
                                        </View>
                                    ))}
                                </ScrollView>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingTop: 16, borderTop: '1px solid #e2e8f0', marginTop: 12 }}>
                                    <Text style={{ fontSize: 13, color: '#64748b' }}>{overviewJobs.length} jobs shown  \u00b7  {overviewJobs.filter(j => j.reviewed).length} included</Text>
                                    <View style={{ flexDirection: 'row', gap: 16 }}>
                                        <Text style={{ fontSize: 13, color: '#16a34a' }}>\u2713 = Completed</Text>
                                        <Text style={{ fontSize: 13, color: '#d1d5db' }}>\u2717 = Missing</Text>
                                    </View>
                                </View>
                            </>
                        )}
                    </View>
                </View>
            ) : activeTab === 'Equipment Rates' ? (
                <View style={{ margin: 20 }}>
                    <View style={{ backgroundColor: 'white', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)', padding: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 6 }}>Equipment Rates</Text>
                        <Text style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Upload an Excel file with columns: Equipment Tier, Asset Description, Current Hourly/Daily/Weekly Billing Rate</Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                            <input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={(e) => {
                                    setEquipmentUploadStatus(null);
                                    uploadEquipmentRates(e.target.files[0]);
                                    e.target.value = '';
                                }}
                                style={{ fontSize: 13 }}
                            />
                            <Pressable onPress={loadEquipmentRates} style={{ backgroundColor: '#0b142e', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 6 }}>
                                <Text style={{ color: '#fff', fontSize: 13 }}>Refresh</Text>
                            </Pressable>
                        </View>
                        {equipmentUploadStatus ? <Text style={{ fontSize: 13, color: equipmentUploadStatus.startsWith('Missing') ? '#dc2626' : '#16a34a', marginBottom: 12 }}>{equipmentUploadStatus}</Text> : null}
                        {equipmentLoading ? <Text style={{ fontSize: 13, color: '#64748b' }}>Loading...</Text> : (
                            <View>
                                <View style={{ flexDirection: 'row', backgroundColor: '#1e293b', borderRadius: '6px 6px 0 0', paddingHorizontal: 8, paddingVertical: 8 }}>
                                    <Text style={{ width: 50, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>#</Text>
                                    <Text style={{ width: 220, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>Equipment Tier</Text>
                                    <Text style={{ width: 220, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>Asset Description</Text>
                                    <Text style={{ width: 130, fontSize: 11, fontWeight: '600', color: '#f1f5f9', textAlign: 'right' }}>Hourly Rate</Text>
                                    <Text style={{ width: 130, fontSize: 11, fontWeight: '600', color: '#f1f5f9', textAlign: 'right' }}>Daily Rate</Text>
                                    <Text style={{ width: 130, fontSize: 11, fontWeight: '600', color: '#f1f5f9', textAlign: 'right' }}>Weekly Rate</Text>
                                </View>
                                <ScrollView style={{ maxHeight: 600 }}>
                                    {equipmentRatesData.length === 0 ? (
                                        <Text style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>No equipment rates found for this version. Upload an Excel file to get started.</Text>
                                    ) : equipmentRatesData.map((row, idx) => (
                                        <View key={row.id || idx} style={{ flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6, backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                            <Text style={{ width: 50, fontSize: 12, color: '#94a3b8' }}>{idx + 1}</Text>
                                            <Text style={{ width: 220, fontSize: 12, color: '#1e293b', fontWeight: '500' }}>{row['Equipment Tier'] || ''}</Text>
                                            <Text style={{ width: 220, fontSize: 12, color: '#475569' }}>{row['Asset Description'] || ''}</Text>
                                            <Text style={{ width: 130, fontSize: 12, color: '#1e293b', textAlign: 'right', fontFamily: 'monospace' }}>{row['Current Hourly Billing Rate'] ? fNum(pNum(row['Current Hourly Billing Rate'])) : ''}</Text>
                                            <Text style={{ width: 130, fontSize: 12, color: '#1e293b', textAlign: 'right', fontFamily: 'monospace' }}>{row['Current Daily Billing Rate'] ? fNum(pNum(row['Current Daily Billing Rate'])) : ''}</Text>
                                            <Text style={{ width: 130, fontSize: 12, color: '#1e293b', textAlign: 'right', fontFamily: 'monospace' }}>{row['Current Weekly Billing Rate'] ? fNum(pNum(row['Current Weekly Billing Rate'])) : ''}</Text>
                                        </View>
                                    ))}
                                </ScrollView>
                                <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', paddingHorizontal: 8, paddingVertical: 8, borderTop: '2px solid #e2e8f0' }}>
                                    <Text style={{ width: 50, fontSize: 12 }}></Text>
                                    <Text style={{ width: 220, fontSize: 12, fontWeight: '600', color: '#0f172a' }}>{equipmentRatesData.length} tiers</Text>
                                </View>
                            </View>
                        )}
                    </View>
                </View>
            ) : activeTab === 'Resource Code' ? (
                <View style={{ margin: 20 }}>
                    <View style={{ backgroundColor: 'white', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)', padding: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 6 }}>Resource Code</Text>
                        <Text style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Upload an Excel file with columns: Cost Type, Code, Description. Saved to the current version (BU_ID = all).</Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                            <input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={(e) => {
                                    setResourceCodeUploadStatus(null);
                                    uploadResourceCodes(e.target.files[0]);
                                    e.target.value = '';
                                }}
                                style={{ fontSize: 13 }}
                            />
                            <Pressable onPress={loadResourceCodes} style={{ backgroundColor: '#0b142e', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 6 }}>
                                <Text style={{ color: '#fff', fontSize: 13 }}>Refresh</Text>
                            </Pressable>
                        </View>
                        {resourceCodeUploadStatus ? <Text style={{ fontSize: 13, color: resourceCodeUploadStatus.startsWith('Missing') ? '#dc2626' : '#16a34a', marginBottom: 12 }}>{resourceCodeUploadStatus}</Text> : null}
                        {resourceCodeLoading ? <Text style={{ fontSize: 13, color: '#64748b' }}>Loading...</Text> : (
                            <View>
                                <View style={{ flexDirection: 'row', backgroundColor: '#1e293b', borderRadius: '6px 6px 0 0', paddingHorizontal: 8, paddingVertical: 8 }}>
                                    <Text style={{ width: 50, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>#</Text>
                                    <Text style={{ width: 160, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>Cost Type</Text>
                                    <Text style={{ width: 120, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>Code</Text>
                                    <Text style={{ flex: 1, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>Description</Text>
                                </View>
                                <ScrollView style={{ maxHeight: 600 }}>
                                    {resourceCodeData.length === 0 ? (
                                        <Text style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>No resource codes found for this version. Upload an Excel file to get started.</Text>
                                    ) : resourceCodeData.map((row, idx) => (
                                        <View key={row.id || idx} style={{ flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6, backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                            <Text style={{ width: 50, fontSize: 12, color: '#94a3b8' }}>{idx + 1}</Text>
                                            <Text style={{ width: 160, fontSize: 12, color: '#1e293b', fontWeight: '500' }}>{row['Cost Type'] || ''}</Text>
                                            <Text style={{ width: 120, fontSize: 12, color: '#1e293b' }}>{row['Code'] || ''}</Text>
                                            <Text style={{ flex: 1, fontSize: 12, color: '#475569' }}>{row['Description'] || ''}</Text>
                                        </View>
                                    ))}
                                </ScrollView>
                                <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', paddingHorizontal: 8, paddingVertical: 8, borderTop: '2px solid #e2e8f0' }}>
                                    <Text style={{ width: 50, fontSize: 12 }}></Text>
                                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#0f172a' }}>{resourceCodeData.length} codes</Text>
                                </View>
                            </View>
                        )}
                    </View>

                    <View style={{ backgroundColor: 'white', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)', padding: 20, marginTop: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 6 }}>Resource Code Mapping</Text>
                        <Text style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Upload an Excel file with columns: Cost Type, Code, Description, Code 2, Description 2. Saved to the current version (BU_ID = all).</Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                            <input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={(e) => {
                                    setResourceMapUploadStatus(null);
                                    uploadResourceCodeMapping(e.target.files[0]);
                                    e.target.value = '';
                                }}
                                style={{ fontSize: 13 }}
                            />
                            <Pressable onPress={loadResourceCodeMapping} style={{ backgroundColor: '#0b142e', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 6 }}>
                                <Text style={{ color: '#fff', fontSize: 13 }}>Refresh</Text>
                            </Pressable>
                        </View>
                        {resourceMapUploadStatus ? <Text style={{ fontSize: 13, color: resourceMapUploadStatus.startsWith('Missing') ? '#dc2626' : '#16a34a', marginBottom: 12 }}>{resourceMapUploadStatus}</Text> : null}
                        {resourceMapLoading ? <Text style={{ fontSize: 13, color: '#64748b' }}>Loading...</Text> : (
                            <View>
                                <View style={{ flexDirection: 'row', backgroundColor: '#1e293b', borderRadius: '6px 6px 0 0', paddingHorizontal: 8, paddingVertical: 8 }}>
                                    <Text style={{ width: 50, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>#</Text>
                                    <Text style={{ width: 140, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>Cost Type</Text>
                                    <Text style={{ width: 100, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>Code</Text>
                                    <Text style={{ width: 220, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>Description</Text>
                                    <Text style={{ width: 100, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>Code 2</Text>
                                    <Text style={{ flex: 1, fontSize: 11, fontWeight: '600', color: '#f1f5f9' }}>Description 2</Text>
                                </View>
                                <ScrollView style={{ maxHeight: 600 }}>
                                    {resourceMapData.length === 0 ? (
                                        <Text style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>No mappings found for this version. Upload an Excel file to get started.</Text>
                                    ) : resourceMapData.map((row, idx) => (
                                        <View key={row.id || idx} style={{ flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6, backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                            <Text style={{ width: 50, fontSize: 12, color: '#94a3b8' }}>{idx + 1}</Text>
                                            <Text style={{ width: 140, fontSize: 12, color: '#1e293b', fontWeight: '500' }}>{row['Cost Type'] || ''}</Text>
                                            <Text style={{ width: 100, fontSize: 12, color: '#1e293b' }}>{row['Code'] || ''}</Text>
                                            <Text style={{ width: 220, fontSize: 12, color: '#475569' }}>{row['Description'] || ''}</Text>
                                            <Text style={{ width: 100, fontSize: 12, color: '#1e293b' }}>{row['Code 2'] || ''}</Text>
                                            <Text style={{ flex: 1, fontSize: 12, color: '#475569' }}>{row['Description 2'] || ''}</Text>
                                        </View>
                                    ))}
                                </ScrollView>
                                <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', paddingHorizontal: 8, paddingVertical: 8, borderTop: '2px solid #e2e8f0' }}>
                                    <Text style={{ width: 50, fontSize: 12 }}></Text>
                                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#0f172a' }}>{resourceMapData.length} mappings</Text>
                                </View>
                            </View>
                        )}
                    </View>
                </View>
            ) : activeTab === 'Budgets' ? (
                <View style={{ flexDirection: 'row', margin: 20, backgroundColor: 'white' }}>
                    <Budgets />
                </View>
            ) : activeTab === 'Test Jobs' ? (
                <View style={{ flexDirection: 'row', margin: 20, backgroundColor: 'white' }}>
                    <TestJobs />
                </View> 
            ) : activeTab === 'Users' ? (
                <View style={{ margin: 20 }}>
                    <View style={{ 
                        backgroundColor: 'white',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                        padding: 20
                    }}>
                        <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 10 }}>User Email Configuration</Text>
                        <Text style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>
                            Manage user email addresses and their active status for notifications and access.
                        </Text>
                        <View style={{ 
                            flexDirection: 'row', 
                            padding: 12, 
                            backgroundColor: '#f8fafc',
                            borderRadius: 6,
                            borderBottom: '2px solid #e2e8f0',
                            marginBottom: 10
                        }}>
                            <Text style={[s.cell, { width: 400, fontWeight: '600', color: '#0f172a' }]}>Email Address</Text>
                            <Text style={[s.cell, { width: 100, fontWeight: '600', color: '#0f172a' }]}>Active</Text>
                            <Text style={[s.cell, { width: 100, fontWeight: '600', color: '#0f172a' }]}>Actions</Text>
                        </View>
                        <ScrollView style={{ maxHeight: 500 }}>
                            {userEmails.map((user, index) => (
                                <View key={index} style={{ 
                                    flexDirection: 'row',
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                    paddingVertical: 8,
                                    paddingHorizontal: 10,
                                    borderBottom: '1px solid #e2e8f0'
                                }}>
                                    <Text style={[s.cell, { width: 400, color: '#334155', textAlign: 'left' }]}>{user.email}</Text>
                                    <View style={[s.cell, { width: 100, alignItems: 'center', justifyContent: 'center' }]}>
                                        <input
                                            type="checkbox"
                                            checked={user.active}
                                            onChange={async (e) => {
                                                const updated = [...userEmails];
                                                updated[index].active = e.target.checked;
                                                setUserEmails(updated);
                                                await saveUserEmail(user.email, e.target.checked);
                                            }}
                                            style={{ width: 20, height: 20, cursor: 'pointer' }}
                                        />
                                    </View>
                                    <View style={[s.cell, { width: 100, alignItems: 'center', justifyContent: 'center' }]}>
                                        <Pressable
                                            onPress={async () => {
                                                const updated = userEmails.filter((_, i) => i !== index);
                                                setUserEmails(updated);
                                                await deleteUserEmail(user);
                                            }}
                                            style={{ padding: 4 }}
                                        >
                                            <Text style={{ color: '#ef4444', fontSize: 12 }}>Remove</Text>
                                        </Pressable>
                                    </View>
                                </View>
                            ))}
                        </ScrollView>
                        <View style={{ flexDirection: 'row', marginTop: 20, gap: 10 }}>
                            <TextInput
                                style={{
                                    flex: 1,
                                    height: 40,
                                    borderColor: '#e2e8f0',
                                    borderWidth: 1,
                                    borderRadius: 6,
                                    paddingHorizontal: 12,
                                    fontSize: 14
                                }}
                                placeholder="Enter email address"
                                value={newEmail}
                                onChangeText={setNewEmail}
                            />
                            <Pressable
                                style={{
                                    backgroundColor: '#0b142e',
                                    paddingHorizontal: 20,
                                    paddingVertical: 10,
                                    borderRadius: 6,
                                    justifyContent: 'center'
                                }}
                                onPress={async () => {
                                    if (newEmail && newEmail.includes('@')) {
                                        setUserEmails([...userEmails, { email: newEmail, active: true }]);
                                        await saveUserEmail(newEmail, true);
                                        setNewEmail('');
                                    }
                                }}
                            >
                                <Text style={{ color: '#fff', fontSize: 14 }}>Add User</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            ) : activeTab === 'OH Values' ? (
                <View style={{ margin: 20 }}>
                    <View style={{ 
                        backgroundColor: 'white',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                        padding: 20
                    }}>
                        <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 10 }}>Overhead (OH) Values</Text>
                        <Text style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>
                            Overhead percentage values by team for the selected version.
                        </Text>
                        <View style={{ 
                            flexDirection: 'row', 
                            padding: 12, 
                            backgroundColor: '#f8fafc',
                            borderRadius: 6,
                            borderBottom: '2px solid #e2e8f0',
                            marginBottom: 10
                        }}>
                            <Text style={[s.cell, { width: 300, fontWeight: '600', color: '#0f172a' }]}>Team</Text>
                            <Text style={[s.cell, { width: 200, fontWeight: '600', color: '#0f172a' }]}>Overhead Percent</Text>
                            <Text style={[s.cell, { width: 100, fontWeight: '600', color: '#0f172a' }]}>Actions</Text>
                        </View>
                        <ScrollView style={{ maxHeight: 800 }}>
                            {ohValues.map((oh, index) => (
                                <View key={index} style={{ 
                                    flexDirection: 'row',
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                    paddingVertical: 10,
                                    paddingHorizontal: 10,
                                    borderBottom: '1px solid #e2e8f0',
                                    alignItems: 'center'
                                }}>
                                    <Text style={[s.cell, { width: 300, color: '#334155', textAlign: 'left' }]}>{oh.team}</Text>
                                    {editingOhIndex === index ? (
                                        <TextInput
                                            style={{
                                                width: 200,
                                                height: 32,
                                                borderColor: '#2d6cdf',
                                                borderWidth: 1,
                                                borderRadius: 4,
                                                paddingHorizontal: 8,
                                                fontSize: 14,
                                                marginRight: 10
                                            }}
                                            value={editedPercent}
                                            onChangeText={setEditedPercent}
                                            placeholder="Enter percentage"
                                            autoFocus
                                        />
                                    ) : (
                                        <Text 
                                            style={[s.cell, { width: 200, color: '#334155', textAlign: 'left', cursor: 'pointer' }]}
                                            onClick={() => {
                                                setEditingOhIndex(index);
                                                setEditedPercent(oh.percent);
                                            }}
                                        >
                                            {oh.percent}
                                        </Text>
                                    )}
                                    <View style={{ width: 100, flexDirection: 'row', gap: 6 }}>
                                        {editingOhIndex === index ? (
                                            <>
                                                <Pressable
                                                    onPress={() => saveOhPercent(oh.rowId, oh.team, editedPercent)}
                                                    style={{ padding: 4, backgroundColor: '#10b981', borderRadius: 4 }}
                                                >
                                                    <Text style={{ color: '#fff', fontSize: 11 }}>Save</Text>
                                                </Pressable>
                                                <Pressable
                                                    onPress={() => setEditingOhIndex(null)}
                                                    style={{ padding: 4, backgroundColor: '#6b7280', borderRadius: 4 }}
                                                >
                                                    <Text style={{ color: '#fff', fontSize: 11 }}>Cancel</Text>
                                                </Pressable>
                                            </>
                                        ) : (
                                            <Pressable
                                                onPress={() => deleteTeam(index)}
                                                style={{ padding: 4, backgroundColor: '#ef4444', borderRadius: 4 }}
                                            >
                                                <Text style={{ color: '#fff', fontSize: 11 }}>Delete</Text>
                                            </Pressable>
                                        )}
                                    </View>
                                </View>
                            ))}
                        </ScrollView>
                        <View style={{ flexDirection: 'row', marginTop: 20, gap: 10 }}>
                            <TextInput
                                style={{
                                    flex: 1,
                                    height: 40,
                                    borderColor: '#e2e8f0',
                                    borderWidth: 1,
                                    borderRadius: 6,
                                    paddingHorizontal: 12,
                                    fontSize: 14
                                }}
                                placeholder="Team name"
                                value={newTeamName}
                                onChangeText={setNewTeamName}
                            />
                            <TextInput
                                style={{
                                    width: 150,
                                    height: 40,
                                    borderColor: '#e2e8f0',
                                    borderWidth: 1,
                                    borderRadius: 6,
                                    paddingHorizontal: 12,
                                    fontSize: 14
                                }}
                                placeholder="OH %"
                                value={newTeamPercent}
                                onChangeText={setNewTeamPercent}
                            />
                            <Pressable
                                style={{
                                    backgroundColor: '#0b142e',
                                    paddingHorizontal: 20,
                                    paddingVertical: 10,
                                    borderRadius: 6,
                                    justifyContent: 'center'
                                }}
                                onPress={addTeam}
                            >
                                <Text style={{ color: '#fff', fontSize: 14, padding: 5 }}>Add Team</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            ) : activeTab === 'AI' ? (
                <View style={{ margin: 20 }}>
                    <View style={{
                        backgroundColor: 'white',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                        padding: 20
                    }}>
                        <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 6 }}>AI Insights Prompts</Text>
                        <Text style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                            Each section has its own system prompt — Overview and Summary drive the AI Insights panels, Invoice Backup and Field Ticket drive the chat boxes in the billing export and time card previews. Changes are saved to the database (Forecast_User_Data → "AI Summary" tab) and apply to all users.{aiPromptLoading ? ' Loading current…' : ''}
                        </Text>
                        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
                            {AI_PROMPT_SECTIONS.map(({ id, label }) => (
                                <Pressable
                                    key={id}
                                    onPress={() => { setAiSection(id); setAiPromptSaveStatus(''); }}
                                    style={{
                                        paddingHorizontal: 16,
                                        paddingVertical: 8,
                                        borderRadius: 6,
                                        backgroundColor: aiSection === id ? '#2563eb' : '#e2e8f0',
                                    }}
                                >
                                    <Text style={{ color: aiSection === id ? '#fff' : '#334155', fontSize: 13, fontWeight: '500' }}>{label}</Text>
                                </Pressable>
                            ))}
                        </View>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#334155', marginBottom: 6 }}>System prompt (editable)</Text>
                        <textarea
                            value={aiPromptDrafts[aiSection]}
                            onChange={(e) => {
                                const val = e.target.value;
                                setAiPromptDrafts(prev => ({ ...prev, [aiSection]: val }));
                                setAiPromptSaveStatus('');
                            }}
                            style={{
                                width: '100%',
                                minHeight: 360,
                                padding: 12,
                                fontSize: 13,
                                lineHeight: 1.5,
                                fontFamily: 'monospace',
                                border: '1px solid #e2e8f0',
                                borderRadius: 6,
                                resize: 'vertical',
                                boxSizing: 'border-box',
                            }}
                        />
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#334155', marginTop: 16, marginBottom: 6 }}>
                            User prompt — last data sent for {aiSection} (read-only)
                        </Text>
                        <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                            This is built dynamically from the project data each time the AI insight runs. Open the {aiSection} page first to capture the latest.
                        </Text>
                        <textarea
                            value={aiLastUserPrompt || '(no user prompt captured yet — visit the ' + aiSection + ' page to trigger an AI insight request)'}
                            readOnly
                            style={{
                                width: '100%',
                                minHeight: 260,
                                padding: 12,
                                fontSize: 13,
                                lineHeight: 1.5,
                                fontFamily: 'monospace',
                                border: '1px solid #e2e8f0',
                                borderRadius: 6,
                                resize: 'vertical',
                                boxSizing: 'border-box',
                                backgroundColor: '#f8fafc',
                                color: '#334155',
                            }}
                        />
                        <View style={{ flexDirection: 'row', gap: 10, marginTop: 16, alignItems: 'center' }}>
                            <Pressable
                                disabled={aiPromptSaving}
                                style={{ backgroundColor: aiPromptSaving ? '#475569' : '#0b142e', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 6 }}
                                onPress={async () => {
                                    const val = aiPromptDrafts[aiSection];
                                    setAiPromptSaving(true);
                                    setAiPromptSaveStatus('');
                                    try {
                                        await saveServerPrompt(aiSection, val);
                                        // Best-effort local cache (offline fallback) — never fatal.
                                        try { localStorage.setItem(sectionStorageKey(aiSection), val); } catch { /* ignore */ }
                                        setAiPromptSaveStatus('Saved ' + aiSection + ' for everyone');
                                    } catch (e) {
                                        setAiPromptSaveStatus('Save failed: ' + (e && e.message ? e.message : 'unknown error'));
                                    } finally {
                                        setAiPromptSaving(false);
                                    }
                                }}
                            >
                                <Text style={{ color: '#fff', fontSize: 14 }}>{aiPromptSaving ? 'Saving…' : 'Save'}</Text>
                            </Pressable>
                            <Pressable
                                disabled={aiPromptSaving}
                                style={{ backgroundColor: aiPromptSaving ? '#9ca3af' : '#6b7280', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 6 }}
                                onPress={async () => {
                                    setAiPromptDrafts(prev => ({ ...prev, [aiSection]: defaultPromptFor(aiSection) }));
                                    setAiPromptSaving(true);
                                    setAiPromptSaveStatus('');
                                    try {
                                        await clearServerPrompt(aiSection);
                                        try { localStorage.removeItem(sectionStorageKey(aiSection)); } catch { /* ignore */ }
                                        setAiPromptSaveStatus('Reset ' + aiSection + ' to default for everyone');
                                    } catch (e) {
                                        setAiPromptSaveStatus('Reset failed: ' + (e && e.message ? e.message : 'unknown error'));
                                    } finally {
                                        setAiPromptSaving(false);
                                    }
                                }}
                            >
                                <Text style={{ color: '#fff', fontSize: 14 }}>Reset to Default</Text>
                            </Pressable>
                            {aiPromptSaveStatus ? (
                                <Text style={{ fontSize: 13, color: /failed/i.test(aiPromptSaveStatus) ? '#dc2626' : '#16a34a', marginLeft: 8 }}>{aiPromptSaveStatus}</Text>
                            ) : null}
                        </View>
                    </View>
                </View>
            ) : (
                <>
                    <View style={{ flexDirection: 'row', margin: 20, marginBottom: 0 }}>
                        <Pressable
                            style={{
                                padding: 8,
                                paddingVertical: 10,
                                borderRadius: 5,
                                margin: 3,
                                backgroundColor: '#0b142e',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                width: 174
                            }}
                            onPress={() => {
                                createVersionAction()
                            }}>
                            <Text style={{ color: '#fff', fontSize: 14 }}>Create Version</Text>
                            {waiting ? <View style={{ margin: 10 }} ><Icon name="spinner" size={20} color="#0b142e" /></View> : null}
                        </Pressable>
                    </View>
                    <View style={{flexDirection: 'row' }}>
                        <View style={{ 
                            margin: 20, 
                            backgroundColor: 'white',
                            borderRadius: 8,
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                        }}>
                            <View style={{ 
                                flexDirection: 'row', 
                                padding: 12, 
                                backgroundColor: '#f8fafc',
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                                borderBottom: '2px solid #e2e8f0'
                            }}>
                                <Text style={[s.cell, { width: 80, fontWeight: '600', color: '#0f172a' }]}>Version Id</Text>
                                <Text style={[s.cell, { width: 200, fontWeight: '600', color: '#0f172a' }]}>Start</Text>
                                <Text style={[s.cell, { width: 200, fontWeight: '600', color: '#0f172a' }]}>Finish</Text>
                                <Text style={[s.cell, { width: 300, fontWeight: '600', color: '#0f172a' }]}>Description</Text>
                                <Text style={[s.cell, { width: 200, fontWeight: '600', color: '#0f172a' }]}>Type</Text>
                                <Text style={[s.cell, { width: 300, fontWeight: '600', color: '#0f172a' }]}>Notes</Text>
                                <Text style={[s.cell, { width: 100, fontWeight: '600', color: '#0f172a' }]}>Archived</Text>
                                <Text style={[s.cell, { width: 300, fontWeight: '600', color: '#0f172a' }]}>Unlocked</Text>
                            </View>
                            {versionCleaned.map((v, i) => (
                                <Row key={i} v={{...v, index: i}} />
                            ))}
                        </View>
                    </View>
                </>
            )}
        </View>
    );
}





export default Admin;

